
import React, { useMemo, FC, useState, useRef, useEffect } from "react";
import { MessageSquare, Library, Mic, Captions, Download, UserCircle, X, Sliders, Shield } from "lucide-react";
import type { Subtitle, SubtitleStyle, Clip, Track, Transform, EditingSuggestion, ChatMessage, ImageMask, ChatPendingChoice } from "../types";
import { ChunkStatus } from '../client-merge';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { AspectRatio } from "../media-recorder-exporter";
import { DEFAULT_LIBRARY, GeneratedVoice } from "./RightMenu/constants";
import ChatTab from "./RightMenu/ChatTab";
import ClipContextMenu from "./RightMenu/ClipContextMenu";
import TransformControls from "./RightMenu/TransformControls";
import LibraryTab from "./RightMenu/LibraryTab";
import SyntheContentEditor from "./RightMenu/SyntheContentEditor";
import VoiceoverTab from "./RightMenu/VoiceoverTab";
import SubtitlesTab from "./RightMenu/SubtitlesTab";
import ExportTab from "./RightMenu/ExportTab";
import AccountTab from "./RightMenu/AccountTab";
import { MasterTab } from "./RightMenu/MasterTab";


import { updateClipDeep } from "../utils/clip-helpers";

// Re-export GeneratedVoice for backward compatibility
export type { GeneratedVoice } from "./RightMenu/constants";

interface RightMenuProps {
  activeTab?: string;
  setActiveTab?: (tab: string) => void;
  messages?: ChatMessage[];
  onSendMessage?: (message: string) => void;
  onChoiceSelect?: (choice: 'current' | 'original' | 'yes' | 'no', pendingChoice: ChatPendingChoice) => void;
  onTranscriptionModeChange?: (useGpt4o: boolean) => void;
  onAddLibraryItem?: (item: any, category: any, mode?: string) => void;
  onMasterSaveAndExit?: () => Promise<void>;
  libraryRefreshTrigger?: number;
  editingSuggestions?: EditingSuggestion[];

  isExporting?: boolean;
  exportStatus?: string;
  exportProgress?: number;
  exportDetails?: any[];
  exportedVideos?: { id: string; name: string; urlWebM: string; urlMP4?: string; thumbnailUrl?: string; }[];
  onStartExport?: (options: { mode: string, format: string; compression: string; extension: string; codec: string; }) => void;
  onCancelExport?: () => void;
  exportAspectRatio: AspectRatio;
  onExportAspectRatioChange: (ratio: AspectRatio) => void;
  isTimelineEligibleForFastExport: boolean;
  vfxSegmentPreviews?: { id: string; name: string; blobUrl: string; duration: number }[];
  // Server export props
  onStartServerExport?: (options?: {
    burnSubtitles?: boolean;
    quality?: 'draft' | 'standard' | 'high';
    mode?: 'chunk' | 'full';
    useGPU?: boolean;
    engine?: 'mlt' | 'ffmpeg';
  }) => void;
  activeServerExports?: {
    isActive: boolean;
    jobId?: string;
    status?: 'pending' | 'downloading' | 'processing' | 'encoding' | 'uploading' | 'complete' | 'failed';
    progress?: number;
    currentStep?: string;
    downloadUrl?: string;
    error?: string;
  }[];
  onCancelServerExport?: (jobId?: string) => void;
  onDeleteExport?: (videoId: string, videoUrl: string) => void;
  // Client-side chunk manager
  chunks?: ChunkStatus[];
  isMerging?: boolean;
  onRetryChunk?: (chunkIndex: number) => void;
  // Hybrid export
  onStartHybridExport?: () => void;
  hasTimelineOverlays?: boolean;
  totalTimelineDuration?: number;

  subtitleStyle: SubtitleStyle;
  setSubtitleStyle: React.Dispatch<React.SetStateAction<SubtitleStyle>>;
  onSeekRequest: (time: number) => void;
  activeSubtitleLanguage: string;
  onActiveSubtitleLanguageChange: (lang: string) => void;
  onTranslateSubtitles?: (targetLang: string) => void;
  onGenerateSubtitles?: (clip: Clip) => Promise<void>;
  translationsInProgress?: Set<string>;

  allClips: Clip[];
  setAllClips: React.Dispatch<React.SetStateAction<Clip[]>>;
  selectedClipId: string | null;
  setSelectedClipId: (id: string | null) => void;
  selectedClip?: Clip | null;
  isClipContextMenuOpen?: boolean;
  onCloseClipContextMenu?: () => void;
  onClipVolumeChange?: (volume: number) => void;
  onClipVolumeCommit?: (volume: number) => void;
  onEnhanceClipAudio?: (clip: Clip) => void;
  onSelectAudioSource?: (clip: Clip, sourceType: 'original' | 'enhanced', sourceId?: string) => void;
  onDeleteAudioSource?: (clip: Clip, sourceId: string) => void;
  onEnhanceVideo?: (clip: Clip, prompt: string) => void;
  onCancelEnhanceVideo?: (clip: Clip) => void;
  onSelectVideoVersion?: (clip: Clip, versionId: string | undefined) => void;
  // Image Mask (Nano Banana)
  onGenerateImageMask?: (clip: Clip, prompt: string, useActiveFrame?: boolean | Blob, cropData?: ImageMask['cropRect']) => void;
  onCaptureFrameForMask?: (clip: Clip, useActiveFrame?: boolean) => Promise<Blob | null>;
  onToggleImageMask?: (clip: Clip, maskId: string, active: boolean) => void;
  onDeleteImageMask?: (clip: Clip, maskId: string) => void;
  onAddImageMaskToTimeline?: (clip: Clip, mask: ImageMask) => void;
  tracks: Track[];
  isRestrictedMode?: boolean;
  currentScope?: any;
  onSaveMaster?: (data: any) => void;
  // Voiceover state (managed by App.tsx for persistence)
  generatedVoices?: GeneratedVoice[];
  setGeneratedVoices?: React.Dispatch<React.SetStateAction<GeneratedVoice[]>>;
  voiceoverText?: string;
  setVoiceoverText?: React.Dispatch<React.SetStateAction<string>>;
  selectedSubtitleId?: number | string | null;
  projectName?: string;
  preloadedAssets?: Record<string, string>;
  isLibraryAssetsComplete?: boolean;
}

const RightMenu: FC<RightMenuProps> = ({
  activeTab = "assistant",
  setActiveTab = (..._: any[]) => { },
  messages,
  onSendMessage = (..._: any[]) => { },
  onChoiceSelect = (_choice: 'current' | 'original' | 'yes' | 'no', _pendingChoice: ChatPendingChoice) => { },
  onTranscriptionModeChange,
  onAddLibraryItem = (item, category, mode = "add") => console.warn(`Ajouté (${mode}) : ${item.name}`),
  onMasterSaveAndExit,
  libraryRefreshTrigger = 0,
  editingSuggestions = [],
  isExporting = false,
  exportStatus = "Not exporting",
  exportProgress = 0,
  exportDetails = [],
  exportedVideos = [],
  onStartExport = (..._: any[]) => { },
  onCancelExport = (..._: any[]) => { },
  exportAspectRatio,
  onExportAspectRatioChange,
  isTimelineEligibleForFastExport,
  vfxSegmentPreviews = [],
  onStartServerExport,
  activeServerExports,
  onCancelServerExport,
  onDeleteExport,
  chunks = [],
  isMerging = false,
  onRetryChunk,
  onStartHybridExport,
  hasTimelineOverlays = false,
  totalTimelineDuration = 0,
  subtitleStyle,
  setSubtitleStyle = (..._: any[]) => { },
  onSeekRequest = (..._: any[]) => { },
  activeSubtitleLanguage,
  onActiveSubtitleLanguageChange = (..._: any[]) => { },
  onTranslateSubtitles = (..._: any[]) => { },
  onGenerateSubtitles,
  translationsInProgress = new Set(),
  allClips,
  setAllClips = (..._: any[]) => { },
  selectedClipId,
  setSelectedClipId = (..._: any[]) => { },
  selectedClip = null,
  isClipContextMenuOpen = false,
  onCloseClipContextMenu = () => { },
  onClipVolumeChange,
  onClipVolumeCommit,
  onEnhanceClipAudio,
  onSelectAudioSource,
  onDeleteAudioSource,
  onEnhanceVideo,
  onCancelEnhanceVideo,
  onSelectVideoVersion,
  // Image Mask (Nano Banana)
  onGenerateImageMask,
  onCaptureFrameForMask,
  onToggleImageMask,
  onDeleteImageMask,
  onAddImageMaskToTimeline,
  tracks,
  user,
  scaleFactor = 1,
  setScaleFactor = () => { },
  isRestrictedMode = false,
  currentScope,
  onSaveMaster = (data) => console.log('Save master default', data),
  // Voiceover state from App.tsx
  generatedVoices = [],
  setGeneratedVoices = () => { },
  voiceoverText = "",
  setVoiceoverText = () => { },
  selectedSubtitleId = null,
  projectName = "Projet",
  preloadedAssets = {},
  isLibraryAssetsComplete = false,
}) => {

  // Ref to hold the latest generatedVoices state for cleanup
  const generatedVoicesRef = useRef(generatedVoices);
  useEffect(() => {
    generatedVoicesRef.current = generatedVoices;
  }, [generatedVoices]);

  // Cleanup effect that runs only on unmount
  useEffect(() => {
    return () => {
      // When the component unmounts, revoke all URLs from the latest state
      generatedVoicesRef.current.forEach(voice => {
        if (voice.url && voice.url.startsWith('blob:')) {
          URL.revokeObjectURL(voice.url);
        }
      });
    };
  }, []); // Empty dependency array ensures this runs only on unmount

  const libraryCount = useMemo(() => DEFAULT_LIBRARY.reduce((acc, c) => acc + (c?.items?.length || 0), 0), []);

  const canShowClipContextMenu = useMemo(() => {
    if (!isClipContextMenuOpen || !selectedClip) return false;
    if (selectedClip.type === 'audio') return true;
    if (selectedClip.type !== 'video') return false;
    const url = selectedClip.url || '';
    return !url.toLowerCase().endsWith('.gif');
  }, [isClipContextMenuOpen, selectedClip]);

  const canShowTransformControls = useMemo(() => {
    if (!selectedClip) return false;
    // Audio clips should NOT show TransformControls - they use ClipContextMenu for volume
    if (selectedClip.type === 'audio') return false;
    const isZoomClip = selectedClip.type === 'zoom';
    const hasTransform = isZoomClip ? !!selectedClip.endTransform : !!selectedClip.transform;
    return hasTransform;
  }, [selectedClip]);

  const handleTransformChange = (clipId: string, updates: Partial<Clip>) => {
    // Use updateClipDeep to recursively update nested synthe clips
    setAllClips(prev => updateClipDeep(prev, clipId, c => ({ ...c, ...updates })));
  };

  const tabs = useMemo(() => {
    const allTabs = [
      { id: "assistant", icon: MessageSquare, label: "Chat", title: "Chat" },
      { id: "library", icon: Library, label: "Lib", title: "Library" },
      { id: "voiceover", icon: Mic, label: "Voice", title: "Voiceover" },
      { id: "subtitles", icon: Captions, label: "Sub", title: "Subtitles" },
      { id: "export", icon: Download, label: "Export", title: "Export" },

      { id: "account", icon: UserCircle, label: "User", title: "Compte" },
    ];
    
    // Add Admin Tab if user is admin
    if (user && user.email === 'mrbiscito@gmail.com') {
      allTabs.push({ id: "admin", icon: Shield, label: "Admin", title: "Dashboard Admin" } as any);
    }

    if (user) {
      return allTabs;
    }

    const accountTab = allTabs.find(t => t.id === 'account');
    const exportTab = allTabs.find(t => t.id === 'export');

    const loggedOutTabs = [];
    if (accountTab) loggedOutTabs.push(accountTab);
    if (exportTab) loggedOutTabs.push(exportTab);

    return loggedOutTabs;
  }, [user, isRestrictedMode]);

  const displayedTabs = useMemo(() => {
    let currentTabs = [...tabs];
    if (isRestrictedMode) {
      currentTabs = [
        { id: "master", icon: Sliders, label: "Master", title: "Configuration Master" },
        ...currentTabs.filter(t => t.id !== 'assistant')
      ];
    }
    return currentTabs;
  }, [tabs, isRestrictedMode]);

  const [isMobileOpen, setIsMobileOpen] = useState(false);



  return (
    <>
      {/* Mobile Bubble Toggle */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="md:hidden fixed bottom-6 right-6 z-50 bg-black text-white p-4 rounded-full shadow-xl hover:bg-gray-900 transition-all active:scale-95 flex items-center justify-center"
        aria-label={isMobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
      >
        {isMobileOpen ? <X className="w-6 h-6" /> : <MessageSquare className="w-6 h-6" />}
      </button>

      <aside className={`
        bg-white border-l border-gray-200 flex flex-col shrink-0 text-black
        transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'fixed inset-0 z-40 w-full translate-x-0' : 'hidden md:flex md:w-[30rem] md:static md:translate-x-0'}
      `}>
        <div className="border-b border-gray-200">
          <div className="flex" role="tablist" aria-label="Right sidebar menu">
            {displayedTabs.map((tab) => {
              const isActive = activeTab === tab.id;
              return (
                <button key={tab.id} role="tab" aria-selected={isActive} id={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] uppercase tracking-wider font-semibold transition-all border-r last:border-r-0 border-gray-200 ${isActive ? "bg-black text-white" : "bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-black"}`}
                  title={tab.title}>
                  <div className="relative">
                    <tab.icon className="w-4 h-4" />
                    {tab.id === "library" && libraryCount > 0 && (<span className={`absolute -top-1 -right-2 text-[8px] w-3.5 h-3.5 flex items-center justify-center ${isActive ? "text-gray-400" : "text-gray-400"}`}>{libraryCount}</span>)}
                  </div>
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex-1 min-h-0">
          {activeTab === "master" && isRestrictedMode && currentScope && (
            <MasterTab
              currentScope={currentScope}
              onUpdate={(updates) => handleTransformChange(currentScope.id, updates)}
              onSaveMaster={onSaveMaster}
            />
          )}

          {activeTab === "assistant" && (
            <>
              {/* Chat is always visible */}
              <ChatTab
                messages={messages}
                onSendMessage={onSendMessage}
                onChoiceSelect={onChoiceSelect}
                editingSuggestions={editingSuggestions}
                onTranscriptionModeChange={onTranscriptionModeChange}
              />

              {/* Inspector Modal Overlay - CENTERED */}
              {((selectedClip && (selectedClip.type === 'synthe' || selectedClip.type === 'panel')) || (canShowTransformControls && selectedClip && selectedClip.type !== 'audio')) && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                  {/* Backdrop */}
                  <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={() => setSelectedClipId(null)}
                  />

                  {/* Modal Card - Centered - WHITE - SQUARE */}
                  <div className="relative w-full max-w-md max-h-[80vh] overflow-hidden bg-white rounded-none border border-black shadow-2xl flex flex-col">
                    {/* Header - Simple */}
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
                      <h2 className="text-sm font-semibold text-gray-900">{selectedClip.name}</h2>
                      <button
                        onClick={() => setSelectedClipId(null)}
                        className="p-1.5 hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>

                    {/* Scrollable Content */}
                    <div className="flex-1 overflow-y-auto">
                      {/* Synthé/Panel Content Editor */}
                      {(selectedClip.type === 'synthe' || selectedClip.type === 'panel') && (
                        <SyntheContentEditor
                          clip={selectedClip}
                          onUpdate={handleTransformChange}
                          onSaveMaster={onSaveMaster}
                        />
                      )}

                      {/* Transform Controls */}
                      {canShowTransformControls && (
                        <div className="border-t border-gray-200">
                          <TransformControls
                            clip={selectedClip}
                            onClose={() => setSelectedClipId(null)}
                            onTransformChange={handleTransformChange}
                            onVolumeChange={onClipVolumeChange}
                            onVolumeCommit={onClipVolumeCommit}
                            onEnhanceAudio={onEnhanceClipAudio}
                            onSelectAudioSource={onSelectAudioSource}
                            onDeleteAudioSource={onDeleteAudioSource}
                            onEnhanceVideo={onEnhanceVideo}
                            onCancelEnhanceVideo={onCancelEnhanceVideo}
                            onSelectVideoVersion={onSelectVideoVersion}
                            onGenerateImageMask={onGenerateImageMask}
                            onCaptureFrameForMask={onCaptureFrameForMask}
                            onToggleImageMask={onToggleImageMask}
                            onDeleteImageMask={onDeleteImageMask}
                            onAddImageMaskToTimeline={onAddImageMaskToTimeline}
                            allClips={allClips}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Clip Context Menu - Audio only modal */}
              {canShowClipContextMenu && selectedClip && !(selectedClip.type === 'synthe' || selectedClip.type === 'panel') && !canShowTransformControls && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                  {/* Backdrop */}
                  <div
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    onClick={onCloseClipContextMenu}
                  />
                  {/* Modal Card - Centered - WHITE - SQUARE */}
                  <div className="relative w-full max-w-md max-h-[80vh] overflow-hidden bg-white rounded-none border border-black shadow-2xl flex flex-col">
                    <ClipContextMenu
                      clip={selectedClip}
                      onClose={onCloseClipContextMenu}
                      onVolumeChange={onClipVolumeChange}
                      onVolumeCommit={onClipVolumeCommit}
                      onEnhanceAudio={onEnhanceClipAudio}
                      onSelectAudioSource={onSelectAudioSource}
                      onDeleteAudioSource={onDeleteAudioSource}
                    />
                  </div>
                </div>
              )}
            </>
          )}



          {activeTab === "library" && (
            <LibraryTab
              onAddLibraryItem={onAddLibraryItem}
              allClips={allClips}
              setAllClips={setAllClips}
              tracks={tracks}
              user={user}
              selectedClip={selectedClip}
              onMasterSaveAndExit={onMasterSaveAndExit}
              libraryRefreshTrigger={libraryRefreshTrigger}
              preloadedAssets={preloadedAssets}
              isLibraryAssetsComplete={isLibraryAssetsComplete}
            />
          )}

          {activeTab === "voiceover" && (
            <VoiceoverTab
              onAddLibraryItem={onAddLibraryItem}
              generatedVoices={generatedVoices}
              setGeneratedVoices={setGeneratedVoices}
              voiceoverText={voiceoverText}
              setVoiceoverText={setVoiceoverText}
            />
          )}

          {activeTab === "subtitles" && (
            <SubtitlesTab
              subtitleStyle={subtitleStyle}
              setSubtitleStyle={setSubtitleStyle}
              onSeekRequest={onSeekRequest}
              activeSubtitleLanguage={activeSubtitleLanguage}
              onActiveSubtitleLanguageChange={onActiveSubtitleLanguageChange}
              onTranslateSubtitles={onTranslateSubtitles}
              onGenerateSubtitles={onGenerateSubtitles}
              translationsInProgress={translationsInProgress}
              allClips={allClips}
              setAllClips={setAllClips}
              selectedClipId={selectedClipId}
              setSelectedClipId={setSelectedClipId}
              selectedSubtitleId={selectedSubtitleId}
              projectName={projectName}
            />
          )}

          {activeTab === "export" && (
            <ExportTab
              isExporting={isExporting}
              exportStatus={exportStatus}
              exportProgress={exportProgress}
              exportDetails={exportDetails}
              exportedVideos={exportedVideos}
              onStartExport={onStartExport}
              onCancelExport={onCancelExport}
              exportAspectRatio={exportAspectRatio}
              onExportAspectRatioChange={onExportAspectRatioChange}
              isTimelineEligibleForFastExport={isTimelineEligibleForFastExport}
              vfxSegmentPreviews={vfxSegmentPreviews}
              onStartServerExport={onStartServerExport}
              activeServerExports={activeServerExports}
              onCancelServerExport={onCancelServerExport}
              onDeleteExport={onDeleteExport}
              projectName={projectName}
              chunks={chunks}
              isMerging={isMerging}
              onRetryChunk={onRetryChunk}
              onStartHybridExport={onStartHybridExport}
              hasTimelineOverlays={hasTimelineOverlays}
              totalTimelineDuration={totalTimelineDuration}
            />
          )}



          {activeTab === "account" && (
            <AccountTab
              user={user}
              scaleFactor={scaleFactor}
              setScaleFactor={setScaleFactor}
            />
          )}
        </div>

      </aside >
    </>
  );
}

export default RightMenu;
